// Helpers for the Overview-tab trend chart. Kept as plain functions so they
// can be unit-tested without rendering React.

export function isFullRun(runType) {
  return typeof runType === 'string' && /^full/i.test(runType);
}

export function pivotTrendByProvider(trend) {
  if (!Array.isArray(trend) || trend.length === 0) {
    return { rows: [], providers: [], runTypeByDate: {} };
  }
  const providerSet = new Set();
  const byDate = new Map();
  const runTypeByDate = {};
  trend.forEach((row) => {
    const date = row.snapshot_date;
    if (row.run_type && !runTypeByDate[date]) runTypeByDate[date] = row.run_type;
    const breakdown = Array.isArray(row.value_breakdown)
      ? row.value_breakdown
      : (row.value_breakdown?.providers || []);
    if (!byDate.has(date)) byDate.set(date, { date });
    const bucket = byDate.get(date);
    if (Array.isArray(breakdown) && breakdown.length > 0) {
      breakdown.forEach((p) => {
        const name = p.provider_name || p.display_name || p.provider;
        if (!name) return;
        providerSet.add(name);
        // fn_get_aim_mention_rate_trend's value_breakdown rows key the value as
        // `mention_rate_pct`; older/other payloads used `mention_pct` / `mention_rate`.
        // Check the RPC key first so the per-provider lines aren't flat-zero.
        bucket[name] = Number(p.mention_rate_pct ?? p.mention_pct ?? p.mention_rate ?? 0);
      });
    } else if (row.value_numeric != null) {
      providerSet.add('Overall');
      bucket['Overall'] = Number(row.value_numeric);
    }
  });
  return {
    rows: Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date)),
    providers: Array.from(providerSet),
    runTypeByDate,
  };
}

/** How many providers the Mention Rate Trend shows before the reader opts into more. */
export const DEFAULT_VISIBLE_PROVIDERS = 5;

/**
 * The providers shown by default: the top `limit` by average mention rate across the
 * trend (missing points are skipped, not counted as zero). Drawing all of them at once
 * (11 for a typical run) made the lines an unreadable tangle; the rest stay one click
 * away in the provider toggle. Ties keep the payload order.
 * @param {Array<object>} rows       pivoted rows ({ date, [provider]: pct })
 * @param {string[]} providers
 * @param {number} [limit]
 * @returns {Set<string>}
 */
export function defaultVisibleProviders(rows, providers, limit = DEFAULT_VISIBLE_PROVIDERS) {
  const avg = (p) => {
    const vals = (rows || []).map((r) => r[p]).filter((v) => Number.isFinite(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : -Infinity;
  };
  return new Set(
    (providers || [])
      .map((p, i) => ({ p, i, a: avg(p) }))
      .sort((x, y) => (y.a - x.a) || (x.i - y.i))
      .slice(0, limit)
      .map((x) => x.p),
  );
}

/**
 * The x-axis date to mark as the selected run: its own date when the trend has a point
 * for it, otherwise the nearest trend date. Null when there is no run date or no rows.
 * @param {Array<{date: string}>} rows  sorted ascending
 * @param {string|null} runDate         YYYY-MM-DD (or an ISO timestamp)
 * @returns {string|null}
 */
export function selectedRunMarkerDate(rows, runDate) {
  if (!runDate || !Array.isArray(rows) || rows.length === 0) return null;
  const day = String(runDate).slice(0, 10);
  if (rows.some((r) => r.date === day)) return day;
  const t = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  let best = null;
  rows.forEach((r) => {
    const d = Math.abs(Date.parse(`${r.date}T00:00:00Z`) - t);
    if (best == null || d < best.d) best = { date: r.date, d };
  });
  return best ? best.date : null;
}
